import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Footprints, LogIn, LogOut, User, Wrench, Package, Car, Mail, Plus, X, Users, Trash2, CheckCircle2, AlertTriangle, ShieldCheck, ShieldX, Filter } from "lucide-react";
import { AttachmentPicker } from "@/components/AttachmentPicker";
import { fileSrc } from "@/lib/uploads";
import { DniLookup, NameDniSearch } from "@/components/DniLookup";
import { PlateLookup } from "@/components/PlateLookup";
import { DniBarcodeScanner } from "@/components/DniBarcodeScanner";
import { SiteSelect } from "@/components/SiteSelect";
import { ClearLogsButton } from "@/components/ClearLogsButton";
import { InsideNowList } from "@/components/ActiveVisitsPanel";
import { ExportButtons } from "@/components/ExportButtons"; // Fix BS

const TYPES = [
  { key: "persona", label: "Persona", Ic: User, color: "#64748b" },
  { key: "proveedor", label: "Proveedor", Ic: Wrench, color: "#0891b2" },
  { key: "delivery", label: "Delivery", Ic: Package, color: "#ea580c" },
  { key: "transporte", label: "Transporte", Ic: Car, color: "#7c3aed" },
  { key: "correo", label: "Correo", Ic: Mail, color: "#ca8a04" },
];
const typeMeta = (k) => TYPES.find((t) => t.key === k) || TYPES[0];

const emptyForm = {
  client_id: "", site_id: "", name: "", surname: "", dni: "", direction: "entrada", reason: "",
  visit_type: "persona",
  vehicle_plate: "", vehicle_desc: "", driver_name: "", passenger_name: "",
  company: "", courier: "",
  companions_count: "", companions: [],
  is_authorized: null, authorized_by_person_id: "", authorized_by_name: "",
  recipient_user_id: "",
};

export default function Visits() {
  const { user } = useAuth();
  const backLink = user?.role === "admin" ? "/admin" : user?.role === "supervisor" ? "/supervisor" : "/vigilador";
  const isMobile = user?.role !== "admin";
  const canManage = user?.role === "admin" || user?.role === "supervisor";

  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [photo, setPhoto] = useState([]);
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  const [listFilters, setListFilters] = useState({ client_id: "", site_id: "", date_from: "", date_to: "", direction: "", q: "" });
  const [plateLocalStatus, setPlateLocalStatus] = useState(null);
  const [authorizedPersons, setAuthorizedPersons] = useState([]);
  const [inquilinos, setInquilinos] = useState([]);
  const [dniMatch, setDniMatch] = useState(null);
  // Fix BN (2026-11-03): true solo si nombre y apellido se completaron solos (búsqueda, padrón o escáner).
  // Al cambiar el DNI se limpian SOLO esos; lo que escribió el vigilador a mano no se borra.
  const [autoNames, setAutoNames] = useState(false);
  const [inside, setInside] = useState(null);
  const loadInside = async () => {
    try { const { data } = await api.get("/visits/active"); setInside(data); } catch { /* no bloquea la pantalla */ }
  };
  useEffect(() => { loadInside(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setListFilter = (k, v) => setListFilters((f) => (k === "client_id" ? { ...f, client_id: v, site_id: "" } : { ...f, [k]: v }));
  const clearListFilters = () => setListFilters({ client_id: "", site_id: "", date_from: "", date_to: "", direction: "", q: "" });
  const hasActiveFilters = !!(listFilters.client_id || listFilters.site_id || listFilters.date_from || listFilters.date_to || listFilters.direction || listFilters.q);

  // a) Autocompletar SIN depender de nadie: si esa patente ya ingresó antes
  // a este mismo objetivo, se completa con los datos del último ingreso
  // (gratis, instantáneo). No pisa lo que el usuario ya haya escrito.
  const lookupLocalPlate = async () => {
    const plate = (form.vehicle_plate || "").replace(/[\s-]/g, "").toUpperCase();
    if (!plate || plate.length < 5 || !form.client_id) { setPlateLocalStatus(null); return; }
    try {
      const { data } = await api.get("/visits/vehicle-lookup", { params: { client_id: form.client_id, plate } });
      if (data.found) {
        setForm((f) => ({
          ...f,
          vehicle_desc: f.vehicle_desc || data.vehicle_desc || "",
          driver_name: f.driver_name || data.driver_name || "",
          passenger_name: f.passenger_name || data.passenger_name || "",
        }));
        setPlateLocalStatus("Autocompletado con el último ingreso de esta patente en este objetivo");
      } else {
        setPlateLocalStatus(null);
      }
    } catch { setPlateLocalStatus(null); }
  };
  const setType = (key) => setForm((f) => ({
    ...f, visit_type: key,
    vehicle_plate: "", vehicle_desc: "", driver_name: "", passenger_name: "", company: "", courier: "",
    recipient_user_id: "",
  }));

  useEffect(() => {
    if (!form.client_id) { setAuthorizedPersons([]); setInquilinos([]); return; }
    (async () => {
      try { const { data } = await api.get(`/clients/${form.client_id}/authorized`); setAuthorizedPersons(data); }
      catch { setAuthorizedPersons([]); }
      try { const { data } = await api.get(`/clients/${form.client_id}/inquilinos`); setInquilinos(data); }
      catch { setInquilinos([]); }
    })();
  }, [form.client_id]);

  // Fix AP (2026-09-25): la sede se completa sola con la sede donde el vigilador está
  // trabajando hoy (ronda o turno), igual que en el Check list. Si no se puede
  // deducir queda vacía, y siempre se puede cambiar a mano.
  useEffect(() => {
    if (!form.client_id || user?.role === "admin") return;
    const cid = form.client_id;
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/clients/${cid}/materials/my-site`);
        if (!cancel && data?.site_id) {
          setForm((f) => (f.client_id === cid && !f.site_id ? { ...f, site_id: data.site_id } : f));
        }
      } catch { /* sin sede deducida: se elige a mano */ }
    })();
    return () => { cancel = true; };
  }, [form.client_id, user?.role]);

  const checkDniMatch = (dni) => {
    const clean = (dni || "").replace(/\D/g, "");
    const found = authorizedPersons.find((p) => (p.dni || "").replace(/\D/g, "") === clean);
    setDniMatch(found ? { ok: true, name: `${found.name} ${found.surname}` } : { ok: false });
  };

  const fetchVisits = async (f = listFilters) => {
    try {
      const params = {};
      if (f.client_id) params.client_id = f.client_id;
      if (f.site_id) params.site_id = f.site_id;
      if (f.direction) params.direction = f.direction;
      if (f.date_from) params.date_from = f.date_from;
      if (f.date_to) params.date_to = f.date_to;
      if (f.q) params.q_text = f.q;
      const { data } = await api.get("/visits", { params });
      setList(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const load = async () => { fetchVisits(listFilters); };

  useEffect(() => { (async () => {
    try {
      const c = await api.get("/clients");
      setClients(c.data);
      if (c.data[0]) setForm((f) => ({ ...f, client_id: c.data[0].id }));
    } catch (e) { toast.error(formatApiError(e)); }
  })(); }, []);

  useEffect(() => {
    const t = setTimeout(() => { fetchVisits(listFilters); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listFilters]);

  const removeVisit = async (id) => {
    if (!window.confirm("¿Eliminar este ingreso/visita? No se puede deshacer.")) return;
    try {
      await api.delete(`/visits/${id}`);
      toast.success("Visita eliminada");
      setList((l) => l.filter((v) => v.id !== id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addCompanion = () => setForm((f) => ({ ...f, companions: [...f.companions, { name: "", dni: "" }] }));
  const updCompanion = (i, k, v) => setForm((f) => {
    const companions = f.companions.map((c, idx) => idx === i ? { ...c, [k]: v } : c);
    return { ...f, companions };
  });
  const rmCompanion = (i) => setForm((f) => ({ ...f, companions: f.companions.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault(); if (busy) return;
    if (form.is_authorized === null) return toast.error("Indicá si está autorizado a ingresar o no");
    if (form.is_authorized === true && !form.authorized_by_person_id && !form.authorized_by_name.trim()) {
      return toast.error("Indicá quién autorizó el ingreso");
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        authorized_by_person_id: form.authorized_by_person_id || null,
        authorized_by_name: form.authorized_by_name?.trim() || null,
        recipient_user_id: form.recipient_user_id || null,
        photo_url: photo[0]?.url || null,
        companions_count: form.companions_count === "" ? null : Number(form.companions_count),
        companions: form.companions.filter((c) => c.name?.trim() || c.dni?.trim()),
      };
      await api.post("/visits", payload);
      toast.success(`Visita registrada (${typeMeta(form.visit_type).label} · ${form.direction})`);
      setForm((f) => ({ ...emptyForm, client_id: f.client_id, site_id: f.site_id, visit_type: f.visit_type }));
      setPhoto([]); setDniMatch(null); setAutoNames(false);
      fetchVisits(listFilters);
      loadInside();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const counts = list.reduce((acc, v) => {
    const t = v.visit_type || "persona";
    acc[t] = (acc[t] || 0) + 1; return acc;
  }, {});
  const filtered = filter === "all" ? list : list.filter((v) => (v.visit_type || "persona") === filter);
  const t = form.visit_type;

  return (
    <div style={{ padding: isMobile ? "8px 16px" : "20px 32px", maxWidth: 1200, margin: "0 auto" }}>
      <Link to={backLink} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--invict-text-muted)", fontSize: 12, marginBottom: 12, textDecoration: "none", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}><ArrowLeft size={14}/> Volver</Link>
      <h1 style={{ fontSize: 26, fontWeight: 900, margin: "4px 0 18px", color: "#0A0A0A", letterSpacing: "-0.02em" }}>Visitas e ingresos</h1>

      <InsideNowList data={inside} currentUserId={user?.id} onChanged={async () => { await loadInside(); fetchVisits(listFilters); }} />

      <form onSubmit={submit} className="card" style={{ padding: 16, marginBottom: 18 }} data-testid="visit-form">
        {/* Tipo de ingreso */}
        <div className="field"><label>Tipo de ingreso *</label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(96px, 1fr))", gap: 8 }}>
            {TYPES.map(({ key, label, Ic, color }) => {
              const on = t === key;
              return (
                <button key={key} type="button" onClick={() => setType(key)}
                  style={{ padding: "10px 6px", border: `2px solid ${on ? color : "var(--invict-bone-3)"}`,
                    background: on ? color : "#fff", color: on ? "#fff" : "#0A0A0A", borderRadius: 8,
                    fontWeight: 800, fontSize: 11, cursor: "pointer", display: "flex", flexDirection: "column",
                    alignItems: "center", gap: 5 }}
                  data-testid={`visit-type-${key}`}><Ic size={16}/>{label}</button>
              );
            })}
          </div>
        </div>

        <div className="field"><label>Cliente *</label>
          <select required value={form.client_id} onChange={(e) => { set("client_id", e.target.value); set("site_id", ""); }} data-testid="visit-client">
            <option value="">Elegir...</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <SiteSelect clientId={form.client_id} value={form.site_id} onChange={(v) => set("site_id", v)} testIdPrefix="visit"/>

        {/* Identidad de la persona principal */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
          <div className="field"><label>Nombre *</label><input required value={form.name} onChange={(e) => { set("name", e.target.value); setAutoNames(false); }} data-testid="visit-name"/></div>
          <div className="field"><label>Apellido *</label><input required value={form.surname} onChange={(e) => { set("surname", e.target.value); setAutoNames(false); }} data-testid="visit-surname"/></div>
          <div className="field"><label>DNI *</label><input required value={form.dni} onChange={(e) => {
            const dni = e.target.value;
            // Fix BN: antes borraba nombre y apellido en cada tecla del DNI.
            setForm((f) => (autoNames ? { ...f, dni, name: "", surname: "" } : { ...f, dni }));
            if (autoNames) setAutoNames(false);
          }} data-testid="visit-dni"/></div>
        </div>

        <DniLookup dni={form.dni} testIdPrefix="visit"
          onResolved={({ name, surname }) => { setForm((f) => ({ ...f, name, surname })); setAutoNames(true); }} />
        <NameDniSearch name={form.name} surname={form.surname} testIdPrefix="visit"
          onPick={(p) => { setForm((f) => ({ ...f, dni: p.dni, name: p.name, surname: p.surname })); setAutoNames(true); checkDniMatch(p.dni); }} />

        <div className="field" style={{ margin: "2px 0 6px" }}>
          <DniBarcodeScanner testIdPrefix="visit-dni" onDecoded={(parsed) => {
            setForm((f) => ({ ...f, dni: parsed.dni, name: parsed.name || f.name, surname: parsed.surname || f.surname }));
            setAutoNames(true);
            checkDniMatch(parsed.dni);
          }} />
          {dniMatch && (
            <span data-testid="visit-dni-match-status" style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700,
              color: dniMatch.ok ? "#16a34a" : "#BF3437", marginTop: 6 }}>
              {dniMatch.ok ? <ShieldCheck size={13}/> : <ShieldX size={13}/>}
              {dniMatch.ok ? `Coincide con autorizado: ${dniMatch.name}` : "No coincide con ningún autorizado registrado en este objetivo"}
            </span>
          )}
        </div>

        {/* Campos condicionales por tipo */}
        {t === "proveedor" && (
          <div className="field"><label>Empresa / servicio</label><input value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="ej. Mantenimiento ACME" data-testid="visit-company"/></div>
        )}
        {t === "correo" && (
          <>
            <div className="field"><label>Empresa de mensajería</label><input value={form.courier} onChange={(e) => set("courier", e.target.value)} placeholder="ej. Andreani, OCA, Correo Argentino" data-testid="visit-courier"/></div>
            <div className="field"><label>Destinatario (inquilino)</label>
              <select value={form.recipient_user_id} onChange={(e) => set("recipient_user_id", e.target.value)} data-testid="visit-recipient-select">
                <option value="">Sin destinatario específico</option>
                {inquilinos.map((i) => <option key={i.id} value={i.id}>{i.dwelling_label ? `${i.dwelling_label} · ${i.name}` : i.name}</option>)}
              </select>
              {inquilinos.length === 0 && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 4 }}>Este objetivo no tiene inquilinos registrados todavía. Elegir uno avisa automáticamente en su portal.</div>}
            </div>
          </>
        )}
        {t === "transporte" && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
            <div className="field" style={{ gridColumn: "1 / -1" }}>
              <label>Patente</label>
              <input value={form.vehicle_plate} onChange={(e) => { set("vehicle_plate", e.target.value.toUpperCase()); setPlateLocalStatus(null); }} onBlur={lookupLocalPlate} placeholder="AA123BB" data-testid="visit-vehicle-plate"/>
              {plateLocalStatus && (
                <span data-testid="visit-vehicle-plate-local-status" style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: "#16a34a", marginTop: 5 }}>
                  <CheckCircle2 size={12}/> {plateLocalStatus}
                </span>
              )}
              <PlateLookup plate={form.vehicle_plate} testIdPrefix="visit"
                onResolved={({ brand, model, year }) => set("vehicle_desc", [brand, model, year ? `(${year})` : null].filter(Boolean).join(" "))} />
            </div>
            <div className="field"><label>Vehículo (descripción)</label><input value={form.vehicle_desc} onChange={(e) => set("vehicle_desc", e.target.value)} placeholder="ej. Toyota Etios gris" data-testid="visit-vehicle-desc"/></div>
            <div className="field"><label>Chofer</label><input value={form.driver_name} onChange={(e) => set("driver_name", e.target.value)} placeholder="Nombre del chofer" data-testid="visit-driver-name"/></div>
            <div className="field"><label>Pasajero</label><input value={form.passenger_name} onChange={(e) => set("passenger_name", e.target.value)} placeholder="Nombre del pasajero" data-testid="visit-passenger-name"/></div>
          </div>
        )}

        <div className="field"><label>Dirección</label>
          <div style={{ display: "flex", gap: 8 }}>
            {[{ v: "entrada", l: "Entrada", Ic: LogIn, color: "#16a34a" }, { v: "salida", l: "Salida", Ic: LogOut, color: "#BF3437" }].map(({ v, l, Ic, color }) => (
              <button key={v} type="button" onClick={() => set("direction", v)}
                style={{ flex: 1, padding: 12, border: `2px solid ${form.direction === v ? color : "var(--invict-bone-3)"}`,
                  background: form.direction === v ? color : "#fff", color: form.direction === v ? "#fff" : "#0A0A0A",
                  fontWeight: 800, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
                data-testid={`visit-dir-${v}`}><Ic size={14}/>{l}</button>
            ))}
          </div>
        </div>

        <div className="field"><label>¿Autorizado a ingresar? *</label>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" onClick={() => set("is_authorized", true)}
              style={{ flex: 1, padding: 12, border: `2px solid ${form.is_authorized === true ? "#16a34a" : "var(--invict-bone-3)"}`,
                background: form.is_authorized === true ? "#16a34a" : "#fff", color: form.is_authorized === true ? "#fff" : "#0A0A0A",
                fontWeight: 800, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
              data-testid="visit-authorized-yes"><ShieldCheck size={14}/>Sí, autorizado</button>
            <button type="button" onClick={() => set("is_authorized", false)}
              style={{ flex: 1, padding: 12, border: `2px solid ${form.is_authorized === false ? "#BF3437" : "var(--invict-bone-3)"}`,
                background: form.is_authorized === false ? "#BF3437" : "#fff", color: form.is_authorized === false ? "#fff" : "#0A0A0A",
                fontWeight: 800, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
              data-testid="visit-authorized-no"><ShieldX size={14}/>No autorizado</button>
          </div>
        </div>
        {form.is_authorized === true && (
          <div className="field"><label>¿Quién autorizó el ingreso? *</label>
            <select value={form.authorized_by_person_id} onChange={(e) => {
              const pid = e.target.value;
              const p = authorizedPersons.find((ap) => ap.id === pid);
              setForm((f) => ({ ...f, authorized_by_person_id: pid, authorized_by_name: p ? `${p.name} ${p.surname}` : "" }));
            }} data-testid="visit-authorized-by-select">
              <option value="">Elegir de personal autorizado...</option>
              {authorizedPersons.map((p) => <option key={p.id} value={p.id}>{p.surname}, {p.name}</option>)}
            </select>
            {!form.authorized_by_person_id && (
              <input value={form.authorized_by_name} onChange={(e) => set("authorized_by_name", e.target.value)}
                placeholder="Nombre de quien autorizó (si no está en la lista)" style={{ marginTop: 6 }} data-testid="visit-authorized-by-name"/>
            )}
          </div>
        )}
        {form.is_authorized === false && (
          <div style={{ fontSize: 11, color: "#BF3437", fontWeight: 700, display: "flex", alignItems: "center", gap: 6, margin: "0 0 8px" }} data-testid="visit-not-authorized-note">
            <AlertTriangle size={13}/> Este ingreso quedará registrado como NO AUTORIZADO.
          </div>
        )}

        {/* Acompañantes */}
        <div className="field"><label>Acompañantes (opcional)</label>
          <input type="number" min="0" value={form.companions_count} onChange={(e) => set("companions_count", e.target.value)} placeholder="Cantidad de acompañantes" data-testid="visit-companions-count"/>
          <div style={{ marginTop: 8 }}>
            {form.companions.map((c, i) => (
              <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6 }} data-testid={`visit-companion-row-${i}`}>
                <input value={c.name} onChange={(e) => updCompanion(i, "name", e.target.value)} placeholder="Nombre" style={{ flex: 2 }} data-testid={`visit-companion-name-${i}`}/>
                <input value={c.dni} onChange={(e) => updCompanion(i, "dni", e.target.value)} placeholder="DNI" style={{ flex: 1 }} data-testid={`visit-companion-dni-${i}`}/>
                <button type="button" onClick={() => rmCompanion(i)} style={{ border: "none", background: "var(--invict-bone-3)", borderRadius: 6, padding: "0 10px", cursor: "pointer" }} data-testid={`visit-companion-remove-${i}`}><X size={14}/></button>
              </div>
            ))}
            <button type="button" onClick={addCompanion} style={{ display: "inline-flex", alignItems: "center", gap: 5, border: "1px dashed var(--invict-bone-3)", background: "#fff", borderRadius: 6, padding: "7px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", color: "#0A0A0A" }} data-testid="visit-add-companion"><Plus size={13}/> Agregar acompañante</button>
          </div>
        </div>

        <div className="field"><label>Motivo (opcional)</label><input value={form.reason} onChange={(e) => set("reason", e.target.value)} placeholder="ej. reunión con gerente" data-testid="visit-reason"/></div>
        <div className="field"><label>Foto (DNI, persona o vehículo)</label>
          <AttachmentPicker value={photo} onChange={setPhoto} accept="image" max={1} label="Adjuntar foto"/>
        </div>
        <button type="submit" disabled={busy} className="btn-primary" style={{ width: "100%", padding: 12 }} data-testid="visit-submit">{busy ? "Registrando..." : "Registrar ingreso"}</button>
      </form>

      {/* Filtros de búsqueda del listado (Fix O) */}
      <div className="card" style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", padding: 14, marginBottom: 14 }} data-testid="visit-list-filters">
        <Filter size={16} style={{ color: "#BF3437", alignSelf: "center" }} />
        <div className="field" style={{ marginBottom: 0, flex: "1 1 160px" }}>
          <label>Objetivo</label>
          <select value={listFilters.client_id} onChange={(e) => setListFilter("client_id", e.target.value)} data-testid="visit-list-filter-client">
            <option value="">Todos</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        {listFilters.client_id && <SiteSelect clientId={listFilters.client_id} value={listFilters.site_id} onChange={(v) => setListFilter("site_id", v)} testIdPrefix="visit-list-filter"/>}
        <div className="field" style={{ marginBottom: 0, flex: "1 1 130px" }}>
          <label>Desde</label>
          <input type="date" value={listFilters.date_from} onChange={(e) => setListFilter("date_from", e.target.value)} data-testid="visit-list-filter-date-from"/>
        </div>
        <div className="field" style={{ marginBottom: 0, flex: "1 1 130px" }}>
          <label>Hasta</label>
          <input type="date" value={listFilters.date_to} onChange={(e) => setListFilter("date_to", e.target.value)} data-testid="visit-list-filter-date-to"/>
        </div>
        <div className="field" style={{ marginBottom: 0, flex: "1 1 130px" }}>
          <label>Dirección</label>
          <select value={listFilters.direction} onChange={(e) => setListFilter("direction", e.target.value)} data-testid="visit-list-filter-direction">
            <option value="">Todas</option>
            <option value="entrada">Entrada</option>
            <option value="salida">Salida</option>
          </select>
        </div>
        <div className="field" style={{ marginBottom: 0, flex: "1 1 190px" }}>
          <label>Buscar</label>
          <input value={listFilters.q} onChange={(e) => setListFilter("q", e.target.value)} placeholder="Nombre, apellido, DNI o patente" data-testid="visit-list-filter-search"/>
        </div>
        {hasActiveFilters && (
          <button type="button" className="btn-ghost" onClick={clearListFilters} style={{ padding: "8px 12px", fontSize: 11 }} data-testid="visit-list-filter-clear">Limpiar</button>
        )}
      </div>

      {/* Filtros por tipo */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12, alignItems: "center", justifyContent: "space-between" }} data-testid="visit-filters">
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <FilterChip active={filter === "all"} onClick={() => setFilter("all")} label="Todos" count={list.length} color="#0A0A0A" testid="visit-filter-all"/>
          {TYPES.map(({ key, label, color }) => (
            <FilterChip key={key} active={filter === key} onClick={() => setFilter(key)} label={label} count={counts[key] || 0} color={color} testid={`visit-filter-${key}`}/>
          ))}
        </div>
        {canManage && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <ExportButtons kind="visits" rows={filtered} testIdPrefix="visit" filtersLabel={[
              filter !== "all" && `Tipo: ${typeMeta(filter).label}`,
              listFilters.client_id && `Objetivo: ${clients.find((c) => c.id === listFilters.client_id)?.name || ""}`,
              listFilters.date_from && `Desde ${listFilters.date_from.split("-").reverse().join("/")}`,
              listFilters.date_to && `Hasta ${listFilters.date_to.split("-").reverse().join("/")}`,
              listFilters.direction && `Dirección: ${listFilters.direction}`,
              listFilters.q && `Búsqueda: "${listFilters.q}"`,
            ].filter(Boolean).join(" · ")} />
            <ClearLogsButton endpoint="/visits" clients={clients} label="visitas" onDone={load} testidPrefix="visit" />
          </div>
        )}
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="visit-list">
        {filtered.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "var(--invict-text-muted)" }}><Footprints size={26} style={{ opacity: 0.4, margin: "0 auto 6px" }}/><div style={{ fontSize: 13 }}>Sin ingresos en esta categoría.</div></div>}
        {filtered.map((v) => {
          const meta = typeMeta(v.visit_type || "persona");
          const Ic = meta.Ic;
          return (
            <div key={v.id} style={{ padding: "12px 16px", borderBottom: "1px solid var(--invict-bone-3)", display: "flex", gap: 12, alignItems: "center" }} data-testid={`visit-row-${v.id}`}>
              {v.photo_url
                ? <img src={fileSrc(v.photo_url)} alt="" style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 4 }}/>
                : <div style={{ width: 48, height: 48, borderRadius: 4, background: `${meta.color}1a`, color: meta.color, display: "flex", alignItems: "center", justifyContent: "center" }}><Ic size={20}/></div>}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: "#0A0A0A" }}>{v.surname}, {v.name}</span>
                  <span style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: "#fff", background: meta.color, padding: "2px 7px", borderRadius: 10 }} data-testid={`visit-badge-${v.id}`}>{meta.label}</span>
                  {v.is_authorized === true && (
                    <span className="badge green" data-testid={`visit-auth-badge-${v.id}`} title={v.authorized_by_name || ""}>Autorizado{v.authorized_by_name ? ` · por ${v.authorized_by_name}` : ""}</span>
                  )}
                  {v.is_authorized === false && (
                    <span className="badge red" data-testid={`visit-auth-badge-${v.id}`}>NO AUTORIZADO</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>DNI {v.dni} · {v.client_name} · {new Date(v.created_at).toLocaleString("es-AR")}</div>
                {v.company && <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>Empresa: {v.company}</div>}
                {v.courier && <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>Mensajería: {v.courier}</div>}
                {v.visit_type === "correo" && v.recipient_dwelling_label && (
                  <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }} data-testid={`visit-recipient-${v.id}`}>
                    Destinatario: {v.recipient_dwelling_label} ({v.recipient_name}) ·{" "}
                    {v.picked_up
                      ? <span style={{ color: "#16a34a", fontWeight: 700 }}>Retirado {new Date(v.picked_up_at).toLocaleString("es-AR")}</span>
                      : <span style={{ color: "#d97706", fontWeight: 700 }}>Pendiente de retiro</span>}
                  </div>
                )}
                {(v.vehicle_plate || v.vehicle_desc || v.driver_name || v.passenger_name) && (
                  <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>
                    {v.vehicle_plate && <>🚗 {v.vehicle_plate} </>}{v.vehicle_desc}
                    {v.driver_name && <> · Chofer: {v.driver_name}</>}{v.passenger_name && <> · Pasajero: {v.passenger_name}</>}
                  </div>
                )}
                {(v.companions_count || (v.companions && v.companions.length > 0)) && (
                  <div style={{ fontSize: 11, color: "var(--invict-text-muted)", display: "flex", alignItems: "center", gap: 4 }}>
                    <Users size={11}/> {v.companions_count || v.companions.length} acompañante(s){v.companions?.length ? `: ${v.companions.map((c) => c.name || c.dni).filter(Boolean).join(", ")}` : ""}
                  </div>
                )}
                {v.reason && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", fontStyle: "italic" }}>{v.reason}</div>}
              </div>
              <span className={`badge ${v.direction === "entrada" ? "green" : "red"}`}>{v.direction}</span>
              {canManage && (
                <button type="button" onClick={() => removeVisit(v.id)} title="Eliminar visita"
                  style={{ padding: 7, border: "1px solid var(--invict-bone-3)", background: "white", color: "#BF3437", borderRadius: 2, cursor: "pointer", flexShrink: 0 }}
                  data-testid={`visit-delete-${v.id}`}>
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, label, count, color, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 18,
        border: `1.5px solid ${active ? color : "var(--invict-bone-3)"}`, background: active ? color : "#fff",
        color: active ? "#fff" : "#0A0A0A", fontSize: 12, fontWeight: 800, cursor: "pointer" }}>
      {label}
      <span style={{ fontSize: 10, fontWeight: 900, background: active ? "rgba(255,255,255,0.25)" : "var(--invict-bone-3)", color: active ? "#fff" : "#0A0A0A", borderRadius: 10, padding: "1px 7px" }}>{count}</span>
    </button>
  );
}
